import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faGlobe,
  faHandshake,
  faLightbulb,
  faChartLine,
  faShieldAlt,
  faRocket,
  faUsers
} from '@fortawesome/free-solid-svg-icons';

const values = [
  {
    icon: faTrophy,
    title: 'Excellence',
    description: 'We deliver exceptional results that exceed expectations',
  },
  {
    icon: faGlobe,
    title: 'Global Reach',
    description: 'Connected to Web3 communities worldwide',
  },
  {
    icon: faHandshake,
    title: 'Partnership',
    description: 'Your success is our success',
  },
  {
    icon: faLightbulb,
    title: 'Innovation',
    description: 'Pioneering new marketing strategies in Web3',
  },
];

const capabilities = [
  {
    icon: faChartLine,
    title: 'Data-Driven Strategy',
    description: 'Every decision backed by analytics and market insights'
  },
  {
    icon: faShieldAlt,
    title: 'Security First',
    description: 'Protecting your brand and community at every step'
  },
  {
    icon: faRocket,
    title: 'Rapid Execution',
    description: 'Fast-paced implementation to capture market opportunities'
  },
  {
    icon: faUsers,
    title: 'Community Focus',
    description: 'Building authentic connections with your audience'
  }
];

const About: React.FC = () => {
  return (
    <section id="about" className="py-20 relative overflow-hidden">
      <div className="max-container section-padding relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ type: 'spring' }}
            className="inline-block glass px-4 py-2 rounded-full mb-4"
          >
            <span className="text-sm text-verdigris">About Us</span>
          </motion.div>
          <h2 className="heading-2 text-linen mb-4">
            Leading the <span className="gradient-text">Web3 Revolution</span>
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            ChainReach is at the forefront of blockchain marketing innovation,
            helping Web3 projects reach their full potential through strategic
            marketing and community building.
          </p>
        </motion.div>

        {/* Values Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
        >
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="glass rounded-xl p-6 text-center"
            >
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="w-12 h-12 bg-gradient-to-br from-persian to-verdigris rounded-lg flex items-center justify-center mx-auto mb-4"
              >
                <FontAwesomeIcon icon={value.icon} className="text-white" />
              </motion.div>
              <h3 className="text-lg font-semibold text-linen mb-2">{value.title}</h3>
              <p className="text-linen-400 text-sm">{value.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Our Approach Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="heading-3 text-linen text-center mb-12">
            Our <span className="gradient-text">Approach</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start space-x-4"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-verdigris to-verdigris-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={capability.icon} className="text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-linen mb-2">{capability.title}</h4>
                  <p className="text-linen-400 text-sm">{capability.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-12 text-center mb-20"
        >
          <h3 className="heading-3 text-linen mb-6">Our Mission</h3>
          <p className="text-linen-300 text-lg leading-relaxed max-w-3xl mx-auto">
            To empower blockchain innovators with cutting-edge marketing strategies that
            bridge the gap between revolutionary technology and mainstream adoption. We believe
            in the transformative power of Web3 and are dedicated to helping projects succeed
            in this new digital frontier.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '5+', label: 'Years in Web3' },
              { value: '25+', label: 'Projects Delivered' },
              { value: '50M+', label: 'Total Views' },
              { value: '$1B+', label: 'Market Cap Generated' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl font-bold gradient-text">{stat.value}</div>
                <div className="text-linen-400 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;